import React, { useEffect, useState } from 'react';

import dayjs, { Dayjs } from 'dayjs';

import { KbaAnswers, kbaApi, KbaQuestion } from 'api/KbaApi';
import BlurPopup from 'components/BlurPopup';
import Loader from 'components/Loader/index';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { getApplicationInfoForDashboardThunk } from 'thunks/getApplicationInfoForDashboard';

import FinalStep from './FinalStep';
import InitialStep from './InitialStep';
import SurveyStep from './SurveyStep';
import ExpiredStep from './ExpiredStep';
import styles from './KbaPopup.module.scss';
import * as notificationManager from '../../../../notifications';
import { FailureNotificationType } from '../../../../notifications';

enum KbaSteps {
  Initial = 'Initial',
  Survey = 'Survey',
  Final = 'Final',
  Expired = 'Expired',
}

interface KbaPopupProps {
  isOpen: boolean;
  isBorrower: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const KbaPopup = ({ isOpen, setOpen, isBorrower }: KbaPopupProps) => {
  const [questions, setQuestion] = useState<KbaQuestion[]>([]);
  const [dueDate, setDueDate] = useState<Dayjs>(dayjs());
  const [step, setStep] = useState<KbaSteps>(KbaSteps.Initial);
  const [loading, setLoading] = useState<boolean>(false);
  const { applicationId, variables } = useAppSelector((state) => state.application);
  const { borrowerId } = useAppSelector((state) => state.borrower);

  const borrowerType = isBorrower ? 'borrower' : 'coborrower';
  const borrowerName = isBorrower ? variables?.borrower_first_name : variables?.coborrower_first_name;

  const dispatch = useAppDispatch();
  const onClose = async () => {
    try {
      setLoading(true);
      setOpen(false);
      setStep(KbaSteps.Initial);
      await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setLoading(false);
    }
  };

  const onStart = async () => {
    try {
      if (applicationId) {
        setLoading(true);

        const { questions: questionsResponse, dueAt } = await kbaApi.startKba({
          applicationId: applicationId,
          borrowerType: borrowerType,
        });

        setQuestion(questionsResponse);
        setStep(KbaSteps.Survey);
        setDueDate(dayjs(dueAt));
      }
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setLoading(false);
    }
  };

  const onExpire = async (answers: string[]) => {
    try {
      if (applicationId) {
        setLoading(true);

        const answersWithType: KbaAnswers[] = questions
          .map((question, index) => {
            return {
              text: answers[index],
              questionType: question.questionType,
            };
          })
          .filter((answer) => answer.text);

        await kbaApi.verifyKbaAnswers({
          applicationId: applicationId,
          borrowerType: borrowerType,
          answers: answersWithType,
        });

        setStep(KbaSteps.Expired);
      }
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setLoading(false);
    }
  };

  const onConfirm = async (answers: string[]) => {
    try {
      if (applicationId) {
        setLoading(true);

        const answersWithType: KbaAnswers[] = questions.map((question, index) => {
          return {
            text: answers[index],
            questionType: question.questionType,
          };
        });

        await kbaApi.verifyKbaAnswers({
          applicationId: applicationId,
          borrowerType: borrowerType,
          answers: answersWithType,
        });

        setStep(KbaSteps.Final);
      }
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setLoading(false);
    }
  };

  const checkStartingDate = async () => {
    setLoading(true);

    try {
      if (!applicationId) {
        return;
      }

      const { startedAt, dueAt } = await kbaApi.getStartingTimestamp({
        applicationId: applicationId,
        borrowerType: borrowerType,
      });

      if (!startedAt || !dueAt) {
        return;
      }

      const now = dayjs();
      const isActive = now.isSameOrAfter(dayjs(startedAt)) && now.isSameOrBefore(dayjs(dueAt));

      if (isActive) {
        await onStart();
      } else {
        await onExpire([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && step === KbaSteps.Initial) {
      checkStartingDate();
    }
  }, [isOpen]);

  const renderKbaStepWidget = () => {
    if (loading) {
      return <Loader customContainerClassname={styles.loaderContainer} />;
    }

    switch (step) {
      case KbaSteps.Initial:
        return <InitialStep onStart={onStart} borrowerName={borrowerName} />;
      case KbaSteps.Survey:
        return <SurveyStep questions={questions} onConfirm={onConfirm} onExpire={onExpire} dueDate={dueDate} />;
      case KbaSteps.Final:
        return <FinalStep />;
      case KbaSteps.Expired:
        return <ExpiredStep />;
      default:
        return <InitialStep onStart={onStart} borrowerName={borrowerName} />;
    }
  };

  const isCloseButtonDisplayed = [KbaSteps.Initial, KbaSteps.Final, KbaSteps.Expired].includes(step);

  return (
    <BlurPopup
      id="kba_popup"
      classes={{
        paper: styles.paper,
        container: styles.container,
      }}
      onClose={onClose}
      open={isOpen}
      scroll="body"
      isCloseButtonDisplayed={isCloseButtonDisplayed}
      blurDeep={2}
    >
      {renderKbaStepWidget()}
    </BlurPopup>
  );
};

export default KbaPopup;
